import { connectorRoutingPolicyRegistry, type ConnectorRoutingPolicy } from '../../../core/connectors/routing';

export interface OrgRoutingPolicyEdgeLike {
  type?: unknown;
  attrs?: Record<string, unknown>;
}

export const ORG_ROUTING_POLICY_IDS = {
  primaryReporting: 'org-primary-reporting',
  secondaryReporting: 'org-secondary-reporting',
  overlay: 'org-overlay',
} as const;

export const ORG_PRIMARY_REPORTING_POLICY: ConnectorRoutingPolicy = {
  id: ORG_ROUTING_POLICY_IDS.primaryReporting,
  family: 'org-primary-reporting',
  defaultRoutingType: 'rounded',
  allowedLayoutSources: ['elk'],
  layoutPointTrust: 'validate-visible-geometry',
  obstacleMode: 'domain-occupancy',
  endpointMode: 'domain-ports',
  manualOverride: 'allow-unless-projected',
};

export const ORG_SECONDARY_REPORTING_POLICY: ConnectorRoutingPolicy = {
  id: ORG_ROUTING_POLICY_IDS.secondaryReporting,
  family: 'org-secondary-reporting',
  defaultRoutingType: 'bezier',
  allowedLayoutSources: [],
  layoutPointTrust: 'never',
  obstacleMode: 'visible-shape-interiors',
  endpointMode: 'boundary',
  manualOverride: 'allow',
};

export const ORG_OVERLAY_POLICY: ConnectorRoutingPolicy = {
  id: ORG_ROUTING_POLICY_IDS.overlay,
  family: 'generic-orthogonal',
  defaultRoutingType: 'rounded',
  allowedLayoutSources: [],
  layoutPointTrust: 'never',
  obstacleMode: 'visible-shape-interiors',
  endpointMode: 'boundary',
  manualOverride: 'allow',
};

export const registerOrgConnectorRoutingPolicies = (): void => {
  connectorRoutingPolicyRegistry.register(ORG_PRIMARY_REPORTING_POLICY);
  connectorRoutingPolicyRegistry.register(ORG_SECONDARY_REPORTING_POLICY);
  connectorRoutingPolicyRegistry.register(ORG_OVERLAY_POLICY);
};

const SECONDARY_RELATIONS = new Set([
  'dotted',
  'matrix',
  'dotted_line_reports',
  'matrix_reports',
]);

const NON_PRIMARY_REPORTING_RELATIONS = new Set([
  ...SECONDARY_RELATIONS,
  'assistant_to',
  'team',
  'team_member',
  'occupies',
  'belongs_to',
  'heads',
  'located_at',
  'funded_by',
  'successor_for',
  'mentors',
  'assigned_to',
  'manages_project',
  'is_instance_of',
]);

const NON_PRIMARY_REPORTING_TYPES = new Set([
  'assistant_to',
  'dotted',
  'matrix',
  'team',
  'team_member',
  'occupies',
  'belongs_to',
  'heads',
  'located_at',
  'funded_by',
  'successor_for',
  'mentors',
  'assigned_to',
  'manages_project',
  'is_instance_of',
]);

const edgeRelation = (edge: Pick<OrgRoutingPolicyEdgeLike, 'attrs'>): string | undefined => {
  const attrs = (edge.attrs ?? {}) as Record<string, unknown>;
  return typeof attrs.relation === 'string' ? attrs.relation : undefined;
};

export const isOrgSecondaryReportingEdge = (edge: OrgRoutingPolicyEdgeLike): boolean => {
  const edgeType = String(edge.type ?? '');
  const relation = edgeRelation(edge);
  return edgeType === 'dotted' || edgeType === 'matrix' || Boolean(relation && SECONDARY_RELATIONS.has(relation));
};

export const isOrgPrimaryReportingEdge = (edge: OrgRoutingPolicyEdgeLike): boolean => {
  const edgeType = String(edge.type ?? '');
  const relation = edgeRelation(edge);

  if (NON_PRIMARY_REPORTING_TYPES.has(edgeType)) return false;
  if (relation && NON_PRIMARY_REPORTING_RELATIONS.has(relation)) return false;
  if (edgeType === 'scenario_reporting' || edgeType === 'reports_to') return true;
  if (relation === 'reports_to' || relation === 'reporting') return true;

  return edgeType === 'reporting' && !relation;
};

export const isOrgOverlayRelationshipEdge = (edge: OrgRoutingPolicyEdgeLike): boolean => {
  const edgeType = String(edge.type ?? '');
  const relation = edgeRelation(edge);
  return NON_PRIMARY_REPORTING_TYPES.has(edgeType) || Boolean(relation && NON_PRIMARY_REPORTING_RELATIONS.has(relation));
};

export const resolveOrgRoutingPolicyIdForEdge = (edge: OrgRoutingPolicyEdgeLike): string | undefined => {
  if (isOrgPrimaryReportingEdge(edge)) return ORG_ROUTING_POLICY_IDS.primaryReporting;
  if (isOrgSecondaryReportingEdge(edge)) return ORG_ROUTING_POLICY_IDS.secondaryReporting;
  if (isOrgOverlayRelationshipEdge(edge)) return ORG_ROUTING_POLICY_IDS.overlay;
  return undefined;
};

registerOrgConnectorRoutingPolicies();
